import Cookies from "js-cookie";
import { ApiClient } from "../../api/client";
import { clearBasketCache } from "./basket";
import { safeLocalStorage } from "~/utils/safeStorage";

/**
 * Экшены пользователя. Логика кеширования — как у корзины (см.
 * action-creaters/basket.js): не дёргать бэкенд на каждой навигации, а брать
 * пользователя из localStorage-кеша. Живой запрос идёт только когда это нужно
 * (см. getUser).
 */

const USER_CACHE_KEY = "user";
/** Время жизни кеша пользователя в localStorage — 1 час. */
const USER_CACHE_TTL_MS = 60 * 60 * 1000;
/**
 * Пауза после неудачного запроса — чтобы неудача не превращалась в поток
 * запросов и событий в GlitchTip (та же логика, что у корзины, см.
 * action-creaters/basket.js).
 */
const USER_FAILURE_BACKOFF_MS = 60 * 1000;

/** Момент последней неудачи (таймаут/обрыв), в памяти вкладки. */
let lastUserFailureTs = 0;

/** Прочитать кеш пользователя из localStorage. Возвращает { data, ts } или null. */
function readUserCache() {
  try {
    const raw = safeLocalStorage.getItem(USER_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || typeof parsed.ts !== "number") {
      return null;
    }
    return parsed;
  } catch (e) {
    return null;
  }
}

/** Записать пользователя в кеш localStorage с текущей меткой времени. */
function writeUserCache(data) {
  try {
    safeLocalStorage.setItem(
      USER_CACHE_KEY,
      JSON.stringify({ data: data || null, ts: Date.now() }),
    );
  } catch (e) {
    // localStorage недоступен/переполнен — не критично.
  }
}

/** Сбросить кеш пользователя (например, при выходе из аккаунта). */
function clearUserCache() {
  try {
    safeLocalStorage.removeItem(USER_CACHE_KEY);
  } catch (e) {
    // no-op
  }
}

/**
 * Загрузить пользователя.
 *
 * Живой запрос в бэкенд идёт ТОЛЬКО если:
 *   - force === true — вход/выход из аккаунта, страницы личного кабинета,
 *     гейт авторизации (authMiddleware);
 *   - в localStorage нет кеша пользователя;
 *   - кеш старше USER_CACHE_TTL_MS (1 час).
 * Иначе (обычная навигация по сайту) пользователь берётся из кеша без запроса.
 *
 * @param {boolean} [force=false]
 */
export const getUser = (force = false) => {
  return async (dispatch) => {
    const cache = readUserCache();
    const isFresh = cache && Date.now() - cache.ts < USER_CACHE_TTL_MS;

    // Обычная навигация со свежим кешем — отдаём из кеша, бэкенд не трогаем.
    if (!force && isFresh) {
      dispatch({ type: "SET_USER", payload: cache.data || {} });
      dispatch({ type: "SET_USER_LOADED", payload: true });
      return;
    }

    // Недавняя неудача — не повторяем запрос на каждой странице.
    if (!force && Date.now() - lastUserFailureTs < USER_FAILURE_BACKOFF_MS) {
      if (cache) {
        dispatch({ type: "SET_USER", payload: cache.data || {} });
      }
      dispatch({ type: "SET_USER_LOADED", payload: true });
      return;
    }

    // Пока грузим свежие данные — сразу показываем то, что есть в кеше.
    if (cache) {
      dispatch({ type: "SET_USER", payload: cache.data || {} });
    }

    const { data: user, failed } = await ApiClient.user.getUser();

    // Бэкенд не ответил — это не «гость». Не пишем кеш (иначе залогиненный
    // пользователь на час становится разлогиненным), не затираем то, что уже
    // показано, и не лезем в авто-авторизацию по autoAuthId.
    if (failed) {
      lastUserFailureTs = Date.now();
      dispatch({ type: "SET_USER_LOADED", payload: true });
      return;
    }

    lastUserFailureTs = 0;
    writeUserCache(user);
    dispatch({ type: "SET_USER", payload: user || {} });
    dispatch({ type: "SET_USER_LOADED", payload: true });

    if (!user) {
      const autoAuthId = Cookies.get("autoAuthId");

      if (autoAuthId) {
        const res = await ApiClient.auth.authByCryptoId(autoAuthId);
        if (res?.success) {
          // После авто-авторизации перезагрузимся и возьмём свежего юзера.
          clearUserCache();
          window.location.reload();
        }
      }
    }
  };
};

export const logOut = () => {
  return async () => {
    await ApiClient.user.logOut();
    // Сбрасываем кеш, чтобы не показывать «залогиненного» из localStorage.
    clearUserCache();
    // Корзина после выхода тоже чужая — Bitrix отдаст гостевую.
    clearBasketCache();
  };
};
